import { commandSchema, mapDocumentSchema, mapIdSchema, mindMapSchema, operationIdSchema, parseAt, timestampSchema, } from '@orbit-mind/validators';
import * as z from 'zod';
export const journalRowSchema = z
    .object({
    mapId: mapIdSchema,
    sequence: z.number().int().positive(),
    operationId: operationIdSchema,
    command: commandSchema,
    // 0/1 rather than a boolean, because IndexedDB cannot index a boolean and the outbox query
    // runs on this column. See `database.ts`.
    synced: z.union([z.literal(0), z.literal(1)]),
})
    .strict();
export const checkpointRowSchema = z
    .object({
    mapId: mapIdSchema,
    // A checkpoint at 0 is the legitimate starting state of a map that has never been edited.
    sequence: z.number().int().nonnegative(),
    document: mapDocumentSchema,
    createdAt: timestampSchema,
})
    .strict();
export const mapRowSchema = z
    .object({
    mapId: mapIdSchema,
    map: mindMapSchema,
    title: z.string(),
    updatedAt: timestampSchema,
    deletedAt: timestampSchema.nullable(),
    nodeCount: z.number().int().nonnegative(),
})
    .strict();
/**
 * One line naming what failed, and nothing about what was in the row.
 *
 * A damaged record is going into a log and possibly into a bug report. The field that failed and
 * the rule it broke are what make it diagnosable; the value that broke it is the user's document.
 */
function describe(failure) {
    const first = failure.issues[0];
    if (first === undefined)
        return failure.code;
    return first.path === ''
        ? `${failure.code}: ${first.message}`
        : `${first.path}: ${first.message}`;
}
/**
 * A sequence legible enough to report, or `null`.
 *
 * Read straight off the raw row rather than the parsed one, because the whole point is that the
 * row did not parse — and knowing *which* entry was lost is most of what makes the report useful.
 */
function sequenceOf(row) {
    if (typeof row !== 'object' || row === null)
        return null;
    const value = row.sequence;
    return typeof value === 'number' && Number.isInteger(value) ? value : null;
}
/** Parses journal rows, keeping the good ones and reporting the rest. */
export function readJournalRows(rows) {
    const records = [];
    const damaged = [];
    for (const row of rows) {
        const parsed = parseAt(journalRowSchema, row, 'storage', 'journal entry');
        if (!parsed.ok) {
            damaged.push({ sequence: sequenceOf(row), reason: describe(parsed.error) });
            continue;
        }
        const value = parsed.value;
        records.push({
            mapId: value.mapId,
            sequence: value.sequence,
            operationId: value.operationId,
            // `parseAt` has established every field; this cast describes what was checked rather than
            // asserting anything new. Zod's inferred optionals differ from the command union's under
            // `exactOptionalPropertyTypes`, and `validators`' command drift test is what keeps the two
            // descriptions of a command in step.
            command: value.command,
            synced: value.synced === 1,
        });
    }
    return { records, damaged };
}
export function toJournalRow(record) {
    return {
        mapId: record.mapId,
        sequence: record.sequence,
        operationId: record.operationId,
        command: record.command,
        synced: record.synced ? 1 : 0,
    };
}
/**
 * Parses a checkpoint row.
 *
 * Unlike a journal entry, there is no partial answer here. The checkpoint is the base every
 * entry after it is a delta against, so a damaged one does not cost the user an edit — it costs
 * them the map, and the caller has to be told that rather than handed a map that starts from
 * nowhere.
 */
export function readCheckpointRow(row) {
    const parsed = parseAt(checkpointRowSchema, row, 'storage', 'checkpoint');
    if (!parsed.ok)
        return { ok: false, reason: describe(parsed.error) };
    return {
        ok: true,
        record: {
            mapId: parsed.value.mapId,
            sequence: parsed.value.sequence,
            // Same justification as above: checked, then described.
            document: parsed.value.document,
            createdAt: parsed.value.createdAt,
        },
    };
}
export function toCheckpointRow(record) {
    return {
        mapId: record.mapId,
        sequence: record.sequence,
        document: record.document,
        createdAt: record.createdAt,
    };
}
export function readMapRow(row) {
    const parsed = parseAt(mapRowSchema, row, 'storage', 'map');
    if (!parsed.ok)
        return { ok: false, reason: describe(parsed.error) };
    return {
        ok: true,
        // Checked, then described — the same cast as above.
        summary: { map: parsed.value.map, nodeCount: parsed.value.nodeCount },
    };
}
export function toMapRow(document, nodeCount) {
    return {
        mapId: document.map.id,
        map: document.map,
        // Indexed columns, so the library can sort and filter without parsing a single row.
        title: document.map.title,
        updatedAt: document.map.updatedAt,
        deletedAt: document.map.deletedAt,
        nodeCount,
    };
}
//# sourceMappingURL=rows.js.map